import { Text, XStack, YStack } from "tamagui";
import { colors } from "@styles/colors";
import { TAPPAY_MERCHANT, FEE_COPY, RefundPolicyBody, TapPayProviderLine } from "@components/payment/TapPayCompliance";
import { formatGasPriceRange } from "@utils/func/gasPriceRange";
import { PRACTICE_GAS_PRICE_RANGES } from "@utils/practice/practiceOrder";

/**
 * 練習站首頁「商店資訊」卡（TapPay 商店審核用揭露）。
 *
 * 練習站網址是送 TapPay 審核的商店網址，審核人員會直接開 /practice 檢查：
 * 公司名稱、統一編號、聯繫方式、退換貨／取消政策、商品參考售價（價格範圍）。
 * 文案與公司資料一律重用 TapPayCompliance 的常數（單一事實來源），
 * 價格範圍讀練習 bootstrap 同一份瓶種資料；政策全文在此頁預設展開，審核截圖不必點開。
 */
const Row = ({ label, value }: { label: string; value: string }) => (
  <XStack gap={12} ai="flex-start">
    <Text fontSize={12.5} color={colors.sub} width={64} flexShrink={0} lineHeight={19}>{label}</Text>
    <Text fontSize={13} color={colors.text} fontWeight="600" flex={1} lineHeight={19}>{value}</Text>
  </XStack>
);

const SectionTitle = ({ children }: { children: string }) => (
  <Text fontSize={13} fontWeight="800" color={colors.text}>{children}</Text>
);

const MerchantInfo = () => {
  const { companyName, taxId, supportEmail } = TAPPAY_MERCHANT;
  return (
    <YStack mt={24} bg="#fff" borderRadius={18} p={16} gap={14} borderWidth={1} borderColor={colors.border || "#E7ECF3"}>
      <XStack ai="center" gap={10}>
        <YStack ai="center" jc="center" width={40} height={40} borderRadius={12} bg="#EFF6FF">
          <Text fontSize={22}>🏢</Text>
        </YStack>
        <YStack flex={1}>
          <Text fontSize={17} fontWeight="900" color={colors.grayDark}>商店資訊</Text>
          <Text fontSize={12.5} color={colors.gray} mt={1}>平台營運公司、聯繫方式與退換貨政策</Text>
        </YStack>
      </XStack>

      <YStack gap={8}>
        <Row label="公司名稱" value={companyName} />
        <Row label="統一編號" value={taxId} />
        <Row label="客服信箱" value={supportEmail} />
        <Row label="收費方式" value={FEE_COPY.replace(/^收費方式：/, "")} />
        <TapPayProviderLine fontSize={12.5} />
      </YStack>

      <YStack gap={8} borderTopWidth={1} borderTopColor={colors.borderLight} pt={12}>
        <SectionTitle>參考售價（價格範圍）</SectionTitle>
        <YStack borderRadius={12} borderWidth={1} borderColor={colors.borderLight} overflow="hidden">
          {PRACTICE_GAS_PRICE_RANGES.map(({ kilogram, price_range }, i) => (
            <XStack
              key={kilogram}
              ai="center"
              jc="space-between"
              px={12}
              py={9}
              bg={i % 2 === 0 ? "#fff" : colors.bgGray2}>
              <Text fontSize={13} color={colors.text} fontWeight="600">桶裝瓦斯 {kilogram}kg</Text>
              <Text fontSize={13} color={colors.primary} fontWeight="800">{formatGasPriceRange(price_range) ?? "依瓦斯行報價"}</Text>
            </XStack>
          ))}
        </YStack>
        <Text fontSize={11.5} color={colors.hint} lineHeight={17}>
          單位為元／桶，為平台上各瓦斯行的參考售價區間；實際金額以訂單為準。
        </Text>
      </YStack>

      <YStack gap={10} borderTopWidth={1} borderTopColor={colors.borderLight} pt={12}>
        <SectionTitle>退換貨與取消政策</SectionTitle>
        <RefundPolicyBody />
      </YStack>
    </YStack>
  );
};

export default MerchantInfo;
